import type { FC } from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { APP_FONT_FAMILY, fontScale } from './scalingTokens';

/**
 * Injects the global CSS that sets the app-wide base font-family and
 * font-size on `html` and `body`.  Every other component uses `rem` via
 * `fontScale`, so the root font-size is the single scaling knob.
 *
 * Also paints the body background with the Fluent `colorNeutralBackground2`
 * token so light and dark themes pick up the correct canvas color.
 *
 * Instead of discrete breakpoint steps (which cause visible jumps), we use
 * a CSS `clamp()` that scales the root font-size **smoothly** from
 * `BASE_FONT_SIZE.mobile` to `BASE_FONT_SIZE.ultraWide` across the full
 * viewport range.  The formula is:
 *
 *   font-size: clamp(minPx, vwSlope + intercept, maxPx)
 *
 * where the slope is calculated so the value equals `min` at `BREAKPOINTS.mobile`
 * and `max` at `BREAKPOINTS.ultraWide`.
 *
 * Mount this once, inside `<ThemeProvider>`, before any routed content.
 */
export const AppStyles: FC = () => {
  return (
    <style>{`
      body {
        margin: 0;
        font-family: ${APP_FONT_FAMILY} !important;
        font-size: ${fontScale.body} !important;
        background-color: ${tokens.colorNeutralBackground2};
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }
      .fui-DatePicker__popupSurface,
      .fui-DatePicker__popupSurface * {
        font-size: ${fontScale.caption} !important;
      }
      @media (max-width: 600px) {
        input,
        select,
        textarea,
        .fui-Input__input,
        .fui-Combobox__input,
        .fui-Dropdown__button,
        .fui-SpinButton__input,
        .fui-Textarea__textarea {
          font-size: 16px !important;
        }
        .fui-Combobox__listbox,
        .fui-Dropdown__listbox,
        .fui-Listbox,
        .fui-DatePicker__popupSurface {
          max-width: calc(100vw - 24px) !important;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
          scroll-behavior: auto !important;
        }
      }
    `}</style>
  );
};

/**
 * Shared page-shell layout mixin.
 *
 * - `page`: outer 1200 px (75 rem) max-width centered container with fluid
 *   horizontal padding (24 px desktop, 16 px below the tablet breakpoint).
 * - `section`: vertical rhythm — 24 px gap between sibling cards.
 * - `cardInterior`: 24 px desktop / 16 px mobile interior padding for cards.
 *
 * Use inside any layout/screen via `const styles = usePageSectionStyles();`
 * then `className={styles.page}` / `styles.section` / `styles.cardInterior`.
 */
export const usePageSectionStyles = makeStyles({
  page: {
    width: '100%',
    maxWidth: '75rem',
    marginInline: 'auto',
    paddingInline: tokens.spacingHorizontalL,
    paddingBlock: tokens.spacingVerticalL,
    boxSizing: 'border-box',
    '@media (max-width: 768px)': {
      paddingInline: tokens.spacingHorizontalM,
    },
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  cardInterior: {
    padding: tokens.spacingHorizontalL,
    '@media (max-width: 768px)': {
      padding: tokens.spacingHorizontalM,
    },
  },
  canvas: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
});
